import { locationUtil, textUtil } from '@grafana/data';
import { config } from '@grafana/runtime';

import { SAVED_QUERIES_BASE_URL } from '../routes';

/**
 * Safely read a `savedQueryReturnTo` value carried on the Explore URL.
 *
 * Mirrors alerting's `useReturnTo`: sanitize, require same-origin http(s), then additionally
 * restrict the path to the Saved Queries pages (the only writers of this param). Rejects absolute
 * external URLs, protocol-relative URLs (`//evil.com`), `javascript:` / other schemes, and
 * unexpected internal routes so a crafted Explore link cannot redirect after Save/Discard.
 */
export function getSafeSavedQueryReturnTo(returnTo: string | null | undefined): string | undefined {
  if (!returnTo) {
    return undefined;
  }

  const sanitized = textUtil.sanitizeUrl(returnTo);
  const baseUrl = `${window.location.origin}/${config.appSubUrl ?? ''}`;

  let url: URL;
  try {
    url = new URL(sanitized, baseUrl);
  } catch {
    return undefined;
  }

  if (!['http:', 'https:'].includes(url.protocol) || url.origin !== window.location.origin) {
    return undefined;
  }

  const relative = locationUtil.stripBaseFromUrl(`${url.pathname}${url.search}`);
  if (!isSavedQueriesPath(relative)) {
    return undefined;
  }

  return relative;
}

/** Surface an Explore round trip started from, for analytics attribution on the return leg. */
export type SavedQueryEditOrigin = 'saved-queries-page' | 'saved-queries-detail-page' | 'explore';

/**
 * Recover which surface sent the user to Explore, from the (already sanitized) return path.
 * The list page writes `/saved-queries?editQuery=<uid>`; the detail page writes
 * `/saved-queries/<uid>?editQuery=true`; the in-Explore library drawer writes nothing.
 */
export function getSavedQueryOrigin(returnTo: string | null | undefined): SavedQueryEditOrigin {
  if (!returnTo) {
    return 'explore';
  }
  if (returnTo.startsWith(`${SAVED_QUERIES_BASE_URL}/`)) {
    return 'saved-queries-detail-page';
  }
  if (isSavedQueriesPath(returnTo)) {
    return 'saved-queries-page';
  }
  return 'explore';
}

function isSavedQueriesPath(path: string): boolean {
  return (
    path === SAVED_QUERIES_BASE_URL ||
    path.startsWith(`${SAVED_QUERIES_BASE_URL}/`) ||
    path.startsWith(`${SAVED_QUERIES_BASE_URL}?`)
  );
}
